// «Quién lo usa»: which agents have this document, and what each may do with
// it (permisos-sobre-knowledge, 7.2, 7.3).
//
// It sits inside the document's dialog, next to `ComoLoTrataElAgente`, and for
// the same reason everything here is inline: this is already a dialog, and the
// standard forbids a modal inside another. So changing a permission asks in the
// row itself -- «¿Cambiar a Restringido?» with Confirm and Cancel -- instead of
// opening a confirmation on top.
//
// Only the agents that have the document are listed: with many agents, listing
// every one buried the few that matter (Daniel, 2026-09-30). The rest are
// reached through «Dar acceso a otro agente», a search that adds one row.
//
// The Brain is not in that list: it administers every document, so giving it one
// means nothing. What can be said about it is «Ocultar al Brain», which is the
// restricted permission on its row -- a switch here because the Brain has no
// editor where it could be set (Daniel, 2026-09-30).
//
// It does not reimplement the assignment: it changes the agent's set through
// the same call the agent editor uses (`POST /agents/{id}/documents/`).

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { log } from '@/lib/logger'
import { type DocumentAgent, useDocumentAgentsQuery, useSetDocumentPermission } from '@/queries/documents'
import type { DocumentPermission } from '@/types/agent'

const NONE = 'none'
const OPTIONS = [NONE, 'leer', 'editar'] as const
const LABEL: Record<string, string> = {
  [NONE]: 'agent_form.permisos.ninguno',
  leer: 'agent_form.permisos.leer',
  editar: 'agent_form.permisos.editar',
}

interface Props {
  /** The document being looked at. `null` while there is none. */
  documentId: number | null
}

export function WhoUsesIt({ documentId }: Props) {
  if (documentId === null) return null
  return <WhoUsesThisOne documentId={documentId} />
}

function WhoUsesThisOne({ documentId }: { documentId: number }) {
  const { t } = useTranslation()
  const { data: everyone = [], isLoading } = useDocumentAgentsQuery(documentId)
  const brain = everyone.find((agent) => agent.is_brain)
  const agents = everyone.filter((agent) => !agent.is_brain)
  const hiddenFromBrain = brain?.permiso === 'restringido'
  const setPermission = useSetDocumentPermission(documentId)
  const [pending, setPending] = useState<{ agentId: string; value: string } | null>(null)
  const [adding, setAdding] = useState(false)
  const [search, setSearch] = useState('')

  // A restricted row left from before counts as not having it: that is what it does.
  const has = (agent: DocumentAgent) => agent.permiso === 'leer' || agent.permiso === 'editar'
  const shown = agents.filter((agent) => has(agent) || agent.agent_id === pending?.agentId)
  const others = agents.filter((agent) => !has(agent) && agent.agent_id !== pending?.agentId)
  const found = others.filter((agent) => agent.agent_name.toLowerCase().includes(search.trim().toLowerCase()))

  const give = (agentId: string) => {
    setPending({ agentId, value: 'leer' })
    setAdding(false)
    setSearch('')
  }

  const confirm = async () => {
    if (!pending) return
    try {
      await setPermission.mutateAsync({
        agentId: pending.agentId,
        permiso: pending.value === NONE ? null : (pending.value as DocumentPermission),
      })
      toast.success(t('knowledge.quien_lo_usa.cambiado'))
    } catch (error) {
      log.error('WhoUsesIt: could not change the permission', error, { component: 'WhoUsesIt' })
      toast.error(t('knowledge.quien_lo_usa.no_cambiado'))
    } finally {
      setPending(null)
    }
  }

  return (
    <div className="space-y-2">
      <Label>{t('knowledge.quien_lo_usa.titulo')}</Label>
      {isLoading ? (
        <p className="text-xs text-muted-foreground">{t('knowledge.quien_lo_usa.cargando')}</p>
      ) : (
        <>
          {shown.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('knowledge.quien_lo_usa.nadie')}</p>
          )}
          <div className="space-y-1">
            {shown.map((agent) => {
              const asking = pending?.agentId === agent.agent_id ? pending : null
              return (
                <div key={agent.agent_id} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="min-w-0 flex-1 truncate text-sm">{agent.agent_name}</p>
                    <Select
                      value={asking?.value ?? (has(agent) ? (agent.permiso ?? NONE) : NONE)}
                      onValueChange={(value) =>
                        setPending(
                          value === (has(agent) ? agent.permiso : NONE) ? null : { agentId: agent.agent_id, value },
                        )
                      }
                      disabled={setPermission.isPending}
                    >
                      <SelectTrigger
                        className="w-40 shrink-0"
                        aria-label={t('knowledge.quien_lo_usa.permiso_de', { agente: agent.agent_name })}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OPTIONS.map((option) => (
                          <SelectItem key={option} value={option}>{t(LABEL[option])}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {asking && (
                    <div role="status" className="flex flex-wrap items-center gap-2 rounded-md bg-warning/10 px-2 py-1">
                      <p className="flex-1 text-xs text-warning">
                        {!has(agent)
                          ? t('knowledge.quien_lo_usa.pregunta_dar', { agente: agent.agent_name, permiso: t(LABEL[asking.value]) })
                          : t('knowledge.quien_lo_usa.pregunta', { agente: agent.agent_name, permiso: t(LABEL[asking.value]) })}
                      </p>
                      <Button type="button" size="xs" variant="ghost" onClick={() => setPending(null)}>
                        {t('knowledge.quien_lo_usa.cancelar')}
                      </Button>
                      <Button type="button" size="xs" onClick={() => void confirm()} disabled={setPermission.isPending}>
                        {t('knowledge.quien_lo_usa.confirmar')}
                      </Button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          {adding ? (
            <div className="space-y-1">
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('knowledge.quien_lo_usa.buscar')}
                aria-label={t('knowledge.quien_lo_usa.buscar')}
                className="h-8 text-sm"
              />
              <div className="max-h-48 overflow-y-auto">
                {found.length === 0 ? (
                  <p className="px-2 py-1 text-xs text-muted-foreground">{t('knowledge.quien_lo_usa.sin_mas')}</p>
                ) : (
                  found.map((agent) => (
                    <Button
                      key={agent.agent_id}
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start font-normal"
                      onClick={() => give(agent.agent_id)}
                    >
                      {agent.agent_name}
                    </Button>
                  ))
                )}
              </div>
              <Button type="button" size="xs" variant="ghost" onClick={() => { setAdding(false); setSearch('') }}>
                {t('knowledge.quien_lo_usa.cancelar')}
              </Button>
            </div>
          ) : (
            others.length > 0 && (
              <Button type="button" size="sm" variant="ghost" onClick={() => setAdding(true)} disabled={setPermission.isPending}>
                <Plus className="h-4 w-4" />
                {t('knowledge.quien_lo_usa.dar_acceso')}
              </Button>
            )
          )}
        </>
      )}
      {brain && !isLoading && (
        <div className="space-y-1 pt-2">
          <div className="flex items-center gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-sm">{t('knowledge.quien_lo_usa.ocultar_brain')}</p>
              <p className="text-xs text-muted-foreground">{t('knowledge.quien_lo_usa.ocultar_brain_ayuda')}</p>
            </div>
            <Switch
              checked={pending?.agentId === brain.agent_id ? pending.value === 'restringido' : hiddenFromBrain}
              onCheckedChange={(hide) =>
                setPending(hide === hiddenFromBrain ? null : { agentId: brain.agent_id, value: hide ? 'restringido' : NONE })
              }
              disabled={setPermission.isPending}
              aria-label={t('knowledge.quien_lo_usa.ocultar_brain')}
            />
          </div>
          {pending?.agentId === brain.agent_id && (
            <div role="status" className="flex flex-wrap items-center gap-2 rounded-md bg-warning/10 px-2 py-1">
              <p className="flex-1 text-xs text-warning">
                {pending.value === 'restringido'
                  ? t('knowledge.quien_lo_usa.pregunta_ocultar')
                  : t('knowledge.quien_lo_usa.pregunta_mostrar')}
              </p>
              <Button type="button" size="xs" variant="ghost" onClick={() => setPending(null)}>
                {t('knowledge.quien_lo_usa.cancelar')}
              </Button>
              <Button type="button" size="xs" onClick={() => void confirm()} disabled={setPermission.isPending}>
                {t('knowledge.quien_lo_usa.confirmar')}
              </Button>
            </div>
          )}
        </div>
      )}
      {shown.length > 0 && <p className="text-xs text-muted-foreground">{t('knowledge.quien_lo_usa.regla')}</p>}
    </div>
  )
}
